/**
 * GameOverScreen - Displayed when all cities are destroyed
 * Shows GAME OVER text, final score, high score entry (if qualified),
 * and navigation options: Play Again, View High Scores, Title Screen.
 */

import { HighScoreManager } from '../utils/HighScoreManager.js';

export class GameOverScreen {
  constructor(game) {
    this.game = game;
    this.score = 0;
    this.wave = 1;
    this.menuOptions = ['Play Again', 'View High Scores', 'Title Screen'];
    this.selectedIndex = 0;
    this.isHighScore = false;
    this.nameEntry = '';
    this.nameSubmitted = false;
    this.highScoreManager = new HighScoreManager();
    this.maxNameLength = 10;
    this.submitCooldown = 0; // Prevents Enter from immediately triggering menu after submit

    // Animation
    this.fadeTimer = 0;
    this.cursorBlink = 0;

    // Starfield for background consistency
    this.stars = [];
    this._initStars(80);

    // Keyboard listener for text entry
    this._keyHandler = (e) => this._onKeyPress(e);
  }

  _initStars(count) {
    this.stars = [];
    for (let i = 0; i < count; i++) {
      this.stars.push({
        x: Math.random(),
        y: Math.random(),
        size: Math.random() * 1.5 + 0.3,
        speed: Math.random() * 0.01 + 0.003,
        brightness: Math.random() * 0.5 + 0.2,
        twinkleSpeed: Math.random() * 2 + 1,
        twinklePhase: Math.random() * Math.PI * 2,
      });
    }
  }

  enter(data = {}) {
    this.score = data.score || 0;
    this.wave = data.wave || 1;
    this.selectedIndex = 0;
    this.nameEntry = '';
    this.nameSubmitted = false;
    this.highScoreManager.refresh();
    this.isHighScore = this.highScoreManager.isHighScore(this.score);
    this.fadeTimer = 0;
    this.cursorBlink = 0;
    this.submitCooldown = 0;

    // Add direct keyboard listener for text input (bypass InputManager for typing)
    window.addEventListener('keydown', this._keyHandler);

    // Update overlay for testing
    this._updateOverlay();
  }

  exit() {
    // Remove keyboard listener
    window.removeEventListener('keydown', this._keyHandler);

    if (this.game.uiOverlay) {
      this.game.uiOverlay.innerHTML = '';
    }
  }

  /**
   * Handle direct keyboard events for name entry
   */
  _onKeyPress(e) {
    // Only handle if in name entry mode (high score, not yet submitted)
    if (!this.isHighScore || this.nameSubmitted) return;

    const key = e.key;

    if (key === 'Enter') {
      // Submit the name
      if (this.nameEntry.length > 0) {
        this._submitName();
        e.preventDefault();
      }
      return;
    }

    if (key === 'Backspace') {
      if (this.nameEntry.length > 0) {
        this.nameEntry = this.nameEntry.slice(0, -1);
        this._updateOverlay();
      }
      e.preventDefault();
      return;
    }

    // Only allow letters, numbers, and some special chars
    if (key.length === 1 && /^[a-zA-Z0-9 _\-.]$/.test(key)) {
      if (this.nameEntry.length < this.maxNameLength) {
        this.nameEntry += key.toUpperCase();
        this._updateOverlay();
      }
      e.preventDefault();
    }
  }

  _submitName() {
    if (this.nameEntry.length > 0 && !this.nameSubmitted) {
      this.highScoreManager.addScore(this.nameEntry, this.score);
      this.nameSubmitted = true;
      this.submitCooldown = 0.3; // Prevent Enter from immediately triggering menu
      this._updateOverlay();
    }
  }

  update(dt) {
    const input = this.game.input;

    // Fade in animation
    this.fadeTimer += dt;
    this.cursorBlink += dt;

    // Animate starfield
    for (const star of this.stars) {
      star.y += star.speed * dt;
      star.twinklePhase += star.twinkleSpeed * dt;
      if (star.y > 1) {
        star.y = 0;
        star.x = Math.random();
      }
    }

    // If still in name entry mode, don't process menu navigation
    if (this.isHighScore && !this.nameSubmitted) {
      // Click on submit button area
      if (input.wasClicked()) {
        const pos = input.getPointerPosition();
        if (this._isOverSubmitButton(pos.x, pos.y) && this.nameEntry.length > 0) {
          this._submitName();
        }
      }
      return;
    }

    // Cooldown after submit to prevent Enter from triggering menu
    if (this.submitCooldown > 0) {
      this.submitCooldown -= dt;
      return;
    }

    // Menu navigation (only when name is submitted or not a high score)
    if (input.isKeyJustPressed('ArrowDown') || input.isKeyJustPressed('KeyS')) {
      this.selectedIndex = (this.selectedIndex + 1) % this.menuOptions.length;
      this._updateOverlay();
    }
    if (input.isKeyJustPressed('ArrowUp') || input.isKeyJustPressed('KeyW')) {
      this.selectedIndex = (this.selectedIndex - 1 + this.menuOptions.length) % this.menuOptions.length;
      this._updateOverlay();
    }
    if (input.isKeyJustPressed('Enter') || input.isKeyJustPressed('Space')) {
      this._selectOption(this.selectedIndex);
    }
    if (input.isKeyJustPressed('Tab')) {
      this.selectedIndex = (this.selectedIndex + 1) % this.menuOptions.length;
      this._updateOverlay();
    }

    // Mouse/touch click
    if (input.wasClicked()) {
      const pos = input.getPointerPosition();
      const clickedIndex = this._getOptionAtPosition(pos.x, pos.y);
      if (clickedIndex !== -1) {
        this._selectOption(clickedIndex);
      }
    }
  }

  _selectOption(index) {
    switch (index) {
      case 0: // Play Again
        this.game.state.setState('playing');
        break;
      case 1: // View High Scores
        this.game.state.setState('highScores');
        break;
      case 2: // Title Screen
        this.game.state.setState('title');
        break;
    }
  }

  _getOptionAtPosition(x, y) {
    const w = this.game.width;
    const h = this.game.height;
    // Menu starts lower when high score entry was shown
    const startY = (this.isHighScore) ? h * 0.68 : h * 0.55;
    const spacing = h * 0.07;
    const hitWidth = Math.max(200, w * 0.4); // Min 200px for touch
    const hitHeight = Math.max(44, spacing * 0.8); // Min 44px for touch targets

    for (let i = 0; i < this.menuOptions.length; i++) {
      const optionY = startY + i * spacing;
      const left = w / 2 - hitWidth / 2;
      const top = optionY - hitHeight / 2;

      if (x >= left && x <= left + hitWidth && y >= top && y <= top + hitHeight) {
        return i;
      }
    }
    return -1;
  }

  _isOverSubmitButton(x, y) {
    const w = this.game.width;
    const h = this.game.height;
    const btnY = h * 0.58;
    const btnWidth = w * 0.15;
    const btnHeight = h * 0.05;
    const left = w / 2 - btnWidth / 2;
    const top = btnY - btnHeight / 2;
    return x >= left && x <= left + btnWidth && y >= top && y <= top + btnHeight;
  }

  /**
   * Update the DOM overlay with game over screen content for Playwright testing
   */
  _updateOverlay() {
    if (!this.game.uiOverlay) return;

    let nameEntryHtml = '';
    if (this.isHighScore && !this.nameSubmitted) {
      nameEntryHtml = `
        <div class="high-score-entry" data-mode="entry">
          <div class="high-score-label">NEW HIGH SCORE!</div>
          <div class="name-input" data-name="${this.nameEntry}">Name: ${this.nameEntry || '(type your name)'}</div>
          <div class="submit-hint">Press ENTER to submit</div>
        </div>
      `;
    } else if (this.isHighScore && this.nameSubmitted) {
      nameEntryHtml = `
        <div class="high-score-entry" data-mode="submitted">
          <div class="high-score-label">SCORE SAVED!</div>
          <div class="saved-name" data-name="${this.nameEntry}">${this.nameEntry} - ${this.score.toLocaleString()}</div>
        </div>
      `;
    } else {
      nameEntryHtml = `<div class="high-score-entry" data-mode="none"></div>`;
    }

    const menuHtml = this.menuOptions.map((opt, i) => {
      const selected = i === this.selectedIndex ? ' data-selected="true"' : '';
      return `<div class="menu-option" data-index="${i}"${selected}>${opt}</div>`;
    }).join('');

    this.game.uiOverlay.innerHTML = `
      <div data-screen="gameOver">
        <div class="game-over-title">GAME OVER</div>
        <div class="final-score" data-score="${this.score}">Score: ${this.score}</div>
        <div class="final-wave" data-wave="${this.wave}">Wave: ${this.wave}</div>
        ${nameEntryHtml}
        <div class="menu-options">${menuHtml}</div>
      </div>
    `;
  }

  render(ctx) {
    const w = this.game.width;
    const h = this.game.height;

    // Background
    this.game.renderer.clearBackground();

    // Starfield
    this._renderStarfield(ctx, w, h);

    // Fade-in alpha
    const alpha = Math.min(this.fadeTimer / 0.5, 1);

    // GAME OVER text with pulsing glow
    const glowPulse = 15 + Math.sin(this.fadeTimer * 3) * 8;
    ctx.save();
    ctx.globalAlpha = alpha;
    this.game.renderer.drawGlowText('GAME OVER', w / 2, h * 0.15, {
      font: `${Math.max(40, w * 0.06)}px "Share Tech Mono", monospace`,
      color: '#ff3333',
      glowColor: '#ff3333',
      glowSize: glowPulse,
    });

    // Final score
    this.game.renderer.drawGlowText(`FINAL SCORE: ${this.score}`, w / 2, h * 0.28, {
      font: `${Math.max(24, w * 0.035)}px "Share Tech Mono", monospace`,
      color: '#ffffff',
      glowColor: '#ffaa00',
      glowSize: 12,
    });

    // Wave reached
    this.game.renderer.drawGlowText(`WAVE REACHED: ${this.wave}`, w / 2, h * 0.35, {
      font: `${Math.max(16, w * 0.02)}px "Share Tech Mono", monospace`,
      color: '#aabbcc',
      glowColor: '#556677',
      glowSize: 5,
    });

    // High score name entry or saved confirmation
    if (this.isHighScore && !this.nameSubmitted) {
      this._renderNameEntry(ctx, w, h);
    } else if (this.isHighScore && this.nameSubmitted) {
      this._renderScoreSaved(ctx, w, h);
    }

    // Menu options (positioned based on whether name entry is shown)
    const menuStartY = this.isHighScore ? h * 0.68 : h * 0.55;
    const spacing = h * 0.07;

    this.menuOptions.forEach((option, i) => {
      const isSelected = i === this.selectedIndex;
      // Dim menu options if still in name entry mode
      const inNameEntry = this.isHighScore && !this.nameSubmitted;
      const menuAlpha = inNameEntry ? 0.3 : 1.0;

      ctx.save();
      ctx.globalAlpha = alpha * menuAlpha;

      // Selection indicator (only when menu is active)
      if (isSelected && !inNameEntry) {
        const indicatorX = w / 2 - w * 0.12;
        this.game.renderer.drawGlowText('\u25B6', indicatorX, menuStartY + i * spacing, {
          font: `${Math.max(14, w * 0.018)}px "Share Tech Mono", monospace`,
          color: '#00ffff',
          glowColor: '#00ffff',
          glowSize: 10,
        });
      }

      this.game.renderer.drawGlowText(option, w / 2, menuStartY + i * spacing, {
        font: `${Math.max(18, w * 0.025)}px "Share Tech Mono", monospace`,
        color: (isSelected && !inNameEntry) ? '#00ffff' : '#aabbcc',
        glowColor: (isSelected && !inNameEntry) ? '#00ffff' : '#445566',
        glowSize: (isSelected && !inNameEntry) ? 15 : 3,
      });

      ctx.restore();
    });

    ctx.restore();

    // Footer
    const footerText = (this.isHighScore && !this.nameSubmitted)
      ? 'TYPE YOUR NAME AND PRESS ENTER'
      : 'USE ARROW KEYS OR CLICK TO SELECT';
    this.game.renderer.drawGlowText(footerText, w / 2, h * 0.93, {
      font: `${Math.max(10, w * 0.012)}px "Share Tech Mono", monospace`,
      color: '#8899aa',
      glowColor: '#445566',
      glowSize: 2,
    });
  }

  _renderNameEntry(ctx, w, h) {
    // "NEW HIGH SCORE!" label
    this.game.renderer.drawGlowText('NEW HIGH SCORE!', w / 2, h * 0.43, {
      font: `${Math.max(18, w * 0.025)}px "Share Tech Mono", monospace`,
      color: '#00ff88',
      glowColor: '#00ff88',
      glowSize: 15,
    });

    // Name entry box
    const boxW = w * 0.4;
    const boxH = h * 0.06;
    const boxX = w / 2 - boxW / 2;
    const boxY = h * 0.50 - boxH / 2;

    // Draw input box border with glow
    ctx.save();
    ctx.strokeStyle = '#00ffff';
    ctx.shadowColor = '#00ffff';
    ctx.shadowBlur = 8;
    ctx.lineWidth = 2;
    ctx.strokeRect(boxX, boxY, boxW, boxH);

    // Semi-transparent background
    ctx.fillStyle = 'rgba(0, 10, 30, 0.8)';
    ctx.shadowBlur = 0;
    ctx.fillRect(boxX + 1, boxY + 1, boxW - 2, boxH - 2);
    ctx.restore();

    // Name text with blinking cursor
    const cursorVisible = Math.floor(this.cursorBlink * 2) % 2 === 0;
    const displayName = this.nameEntry + (cursorVisible ? '_' : ' ');

    this.game.renderer.drawGlowText(displayName, w / 2, h * 0.50, {
      font: `${Math.max(20, w * 0.028)}px "Share Tech Mono", monospace`,
      color: '#ffffff',
      glowColor: '#00ffff',
      glowSize: 5,
    });

    // "ENTER YOUR NAME" label below
    const promptText = this.nameEntry.length === 0 ? 'ENTER YOUR NAME' : 'PRESS ENTER TO SUBMIT';
    this.game.renderer.drawGlowText(promptText, w / 2, h * 0.58, {
      font: `${Math.max(12, w * 0.015)}px "Share Tech Mono", monospace`,
      color: '#8899aa',
      glowColor: '#445566',
      glowSize: 2,
    });
  }

  _renderScoreSaved(ctx, w, h) {
    // "SCORE SAVED!" confirmation
    this.game.renderer.drawGlowText('SCORE SAVED!', w / 2, h * 0.43, {
      font: `${Math.max(18, w * 0.025)}px "Share Tech Mono", monospace`,
      color: '#00ff88',
      glowColor: '#00ff88',
      glowSize: 15,
    });

    this.game.renderer.drawGlowText(`${this.nameEntry} - ${this.score.toLocaleString()}`, w / 2, h * 0.52, {
      font: `${Math.max(16, w * 0.02)}px "Share Tech Mono", monospace`,
      color: '#ffaa00',
      glowColor: '#ffaa00',
      glowSize: 8,
    });
  }

  _renderStarfield(ctx, w, h) {
    for (const star of this.stars) {
      const twinkle = 0.5 + 0.5 * Math.sin(star.twinklePhase);
      const alpha = star.brightness * twinkle;

      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(star.x * w, star.y * h, star.size, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }
}
